<template>
  <div class="show-tonal-audiometry pt-2 pb-3">
    <div class="row">
      <div class="col-md-6 pl-0">
        <h6 class="is-bold text-evollu-gray-light mb-3">
          {{ title }}
        </h6>
      </div>

      <div class="col-md-6 text-right" v-if="value && value.updated_at !== null && value.updated_at !== undefined">
        <small>
          {{ $t('clinical.people.anamnesis.updated_at') }}
        </small>
        <small class="is-bold">
          {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
        </small>
      </div>
    </div>

    <section v-if="value !== null && value !== undefined" class="px-2">

      <div class="evollu-form">
        <table class="table table-responsive table-borderless metrics-table">
          <thead>
            <tr>
              <th class="field"></th>
              <th scope="col" v-for="(column, index) in metricsConfigs.tableColumns" :key="index" class="frequency">
                {{$t(column.label)}}
              </th>
              <th class="masking">
                {{$t('clinical.people.anamnesis.masking')}}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(metric, i) in metricsConfigs.metrics" :key="i">
              <td class="field">
                <span>{{$t(metric.label)}}</span>
              </td>
              <td class="frequency" v-for="(column, j) in metricsConfigs.tableColumns" :key="j">
                <va-input
                  v-if="metric.frequencies.includes(column.value)"
                  :value="getMetricValue(metric.field, metric.ear, column.value)"
                  :disabled="true"
                >
                </va-input>
              </td>
              <td class="masking">
                <span v-if="metric.masking_field !== null">
                  {{getBooleanAnswer(value[metric.masking_field])}}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PTA Right ear -->
      <div class="answer-wrapper">
        <label>
          {{$t('clinical.people.anamnesis.right_ear_avg_areal_hearing_loss')}}:
        </label>
        <span class="is-bold">
          {{getTextAnswer(value.right_ear_avg_areal_hearing_loss)}}
        </span>
      </div>

      <!-- PTA Left ear -->
      <div class="answer-wrapper">
        <label>
          {{$t('clinical.people.anamnesis.left_ear_avg_areal_hearing_loss')}}:
        </label>
        <span class="is-bold">
          {{getTextAnswer(value.left_ear_avg_areal_hearing_loss)}}
        </span>
      </div>

      <!-- Right Hearing Loss Type -->
      <div class="answer-wrapper">
        <label>
          {{$t('clinical.people.anamnesis.right_ear_hearing_loss_type')}}:
        </label>
        <span class="is-bold">
          {{getSingleOptionAnswer(formOptions.tonalAudiometryHearingLossTypes, value.right_ear_hearing_loss_type)}}
        </span>
      </div>

      <!-- Left Hearing Loss Type -->
      <div class="answer-wrapper">
        <label>
          {{$t('clinical.people.anamnesis.left_ear_hearing_loss_type')}}:
        </label>
        <span class="is-bold">
          {{getSingleOptionAnswer(formOptions.tonalAudiometryHearingLossTypes, value.left_ear_hearing_loss_type)}}
        </span>
      </div>

      <!-- Tonal Audiometry Obs -->
      <div class="answer-wrapper">
        <label>
          {{$t('clinical.people.anamnesis.tonal_audiometry_obs')}}:
        </label>
        <span class="is-bold">
          {{getTextAnswer(value.obs)}}
        </span>
      </div>

    </section>

    <section v-else class="px-2">
      <span>{{$t('clinical.people.no_data')}}</span>
    </section>
  </div>

</template>

<script>
import * as AnamnesisOptions from './AnamnesisOptions'
import TonalAudiometryMetricsConfigs from './TonalAudiometryMetricsConfigs'
export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    title: {
      type: String,
      required: false,
      default: function () {
        return this.$t('clinical.people.anamnesis.tonal_audiometry')
      }
    }
  },
  data: function () {
    return {
      formOptions: AnamnesisOptions,
      metricsConfigs: TonalAudiometryMetricsConfigs
    }
  },
  methods: {
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    },
    getTextAnswer: function (answer, defaultValue = 'N/A') {
      return answer || defaultValue
    },
    getBooleanAnswer: function (answer, defaultValue = 'N/A') {
      return answer === null ? defaultValue : (answer === true ? this.$t('yes') : this.$t('no'))
    },
    getSingleOptionAnswer: function (options, answerId, defaultValue = 'N/A') {
      let option = null
      if (answerId !== null) {
        option = options.find(option => option.id === answerId)
        if (option) {
          option = this.$t(option.name)
        }
      }
      return option || defaultValue
    },
    getMetricValue: function (field, ear, frequency) {
      const metric = this.value.metrics.find(metric => metric.field === field && metric.ear === ear && metric.frequency == frequency)
      return metric ? metric.value : null
    },
  }
}
</script>
<style lang="scss">

@mixin for-desktop {
  @media (min-width: 766px) {
    @content;
  }
}

.answer-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
  justify-content: flex-start;

  label {
    margin-bottom: 0rem;
  }

  @include for-desktop {
    flex-direction: row;
    align-items: center;

    label {
      max-width: 80%;
    }

    span {
      margin-left: 0.25rem;
    }
  }
}

.show-tonal-audiometry {

  table.metrics-table {

    width: 100%;
    table-layout: fixed;

    th, td {
      padding: 0.75rem 0.35rem;
    }

    thead {
      color: inherit !important;
      background-color: white !important;

      th {
        color: inherit !important;
      }
    }

    .field {
      text-align: left;
      width: 27.5%;
      min-width: 250px;
    }

    .frequency {
      text-align: center;
      min-width: 40px;
      width: 5%;
    }

    .masking {
      text-align: center;
      width: 10%;
    }

    .va-input-wrapper.va-input input{
      text-align: center;
      padding-left: 0px !important;
    }

  }
}

</style>
